import React, {
  ReactNode,
  useMemo,
  useReducer,
  useEffect,
  useState,
} from 'react';
import signInAuthProvider, {
  apiAccessScope,
  tokenRequest,
  loginRequest,
} from './signInAuthProvider';
import { UserContext, UserDispatchContext } from './internal';
import { User, createUser } from './user';
import LoadingIndicator from './LoadingIndicator';
import axios from 'axios';

import {
  InteractionType,
  EventType,
} from '@azure/msal-browser';
import {
  MsalProvider,
  MsalAuthenticationTemplate,
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react';
import { useAccessToken } from './useAccessToken';
import { useHistory } from 'react-router-dom';
import CustomNavigationClient from './CustomNavigationClient';
import { storeTenant, getStoredTenant } from '../storage/UnitySessionStorage';

export interface UserProviderProps {
  children: ReactNode;
}

const emptyUser: User = {
  name: '',
  firstName: '',
  familyName: '',
  jobTitle: '',
  email: '',
  mobile: '',
  phone: '',
  id: '',
  location: '',
  aadId: '',
  accessToken: '',
  defaultTenantId: '',
  currentTenantId: '',
  tenantIds: [],
  roleIds: [],
  products: [],
  permissions: [],
  can: {},
  statusCode: undefined,
};

const UserProvider = ({ children }: UserProviderProps) => {
  const { accounts } = useMsal();
  signInAuthProvider.setActiveAccount(accounts[0]);
  const idTokenClaims: any = accounts[0]?.idTokenClaims;
  const sub: any = idTokenClaims?.sub;
  const accessToken: any = useAccessToken();
  const [adUserInfo, setAdUserInfo] = useState({});
  const [userInfo, setUserInfo] = useState({});
  const [didLoad, setDidLoad] = useState(false);

  const user = useMemo<User | null>(() => {
    if (adUserInfo && userInfo) {
      return createUser({
        ...userInfo,
        ...adUserInfo,
        accessToken: accessToken,
      });
    }

    return null;
  }, [adUserInfo, userInfo, accessToken]);

  const [userState, dispatch] = useReducer(
    (state: any, action: any) => {
      switch (action.type) {
        case 'SET_USER':
          state = {
            ...state,
            user: action.user,
          };
          break;
        default:
          throw new Error(`Unknown action type ${action.type}`);
      }

      return state.user;
    },
    user ? user : emptyUser
  );

  useEffect(() => {
    const getUserInfo = async () => {
      if (!accessToken) return;

      const aadResponse: any = await axios
        .request({
          method: 'get',
          url: `${process.env.REACT_APP_SECURITY_API_BASE}/users/${sub}`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .catch(e => console.log(e));

      const unityResponse: any = await axios
        .request({
          method: 'get',
          url: `${process.env.REACT_APP_SECURITY_API_BASE}/users/unityuser?sub=${sub}`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .catch(e => {
          console.log(e);
          setUserInfo({ ...emptyUser, statusCode: e?.response?.status });
        });

      if (aadResponse) {
        setAdUserInfo(aadResponse?.data);
      }
      if (unityResponse) {
        if (unityResponse?.data?.defaultTenantId) {
          if (getStoredTenant() == null) {
            storeTenant(unityResponse.data.defaultTenantId);
          }
        }
        setUserInfo({
          ...unityResponse?.data,
          statusCode: unityResponse?.status,
        });
      }
    };
    if (sub) {
      getUserInfo();
    }
  }, [sub, accessToken]);

  useEffect(() => {
    if (!user?.aadId || !user?.statusCode || didLoad) {
      return;
    }
    setDidLoad(true);
    dispatch({ type: 'SET_USER', user });
  }, [user, dispatch, didLoad]);

  return (
    <UserContext.Provider value={userState}>
      <UserDispatchContext.Provider value={dispatch}>
        { children }
      </UserDispatchContext.Provider>
    </UserContext.Provider>
  );
};

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provides msal+univerus authentication to the app
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const history = useHistory();
  const navigationClient = new CustomNavigationClient(history);
  signInAuthProvider.setNavigationClient(navigationClient);

  useEffect(() => {
    const callbackId = signInAuthProvider.addEventCallback((event: any) => {
      if (event.eventType === EventType.LOGIN_FAILURE) {
        if (
          event.error &&
          event.error.errorMessage.indexOf('AADB2C90091') > -1
        ) {
          if (event.interactionType === InteractionType.Redirect) {
            signInAuthProvider.loginRedirect(tokenRequest);
          }
        }
      }
    });

    return () => {
      if (callbackId) {
        signInAuthProvider.removeEventCallback(callbackId);
      }
    };
  }, []);

  return (
    <MsalProvider instance={signInAuthProvider}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
        loadingComponent={() => <LoadingIndicator />}
      >
        <UserProvider>{children}</UserProvider>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
};
